/**
 * プロジェクト管理クラス
 * プロジェクトデータの保存・読み込み・更新・削除を管理
 */

import type { StoredProject, BaseManager } from './types'

export class ProjectsManager implements BaseManager {
  constructor(public db: IDBDatabase | null) {}

  async saveProject(project: Omit<StoredProject, 'id' | 'createdAt' | 'updatedAt'>): Promise<StoredProject> {
    if (!this.db) throw new Error('Database not initialized')

    const newProject: StoredProject = {
      ...project,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite')
      const store = transaction.objectStore('projects')
      const request = store.add(newProject)

      request.onsuccess = () => resolve(newProject)
      request.onerror = () => reject(new Error('Failed to save project'))
    })
  }

  async getProjects(): Promise<StoredProject[]> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readonly')
      const store = transaction.objectStore('projects')
      const index = store.index('updatedAt')
      const request = index.openCursor(null, 'prev') // 更新日時の降順

      const projects: StoredProject[] = []
      
      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          projects.push(cursor.value)
          cursor.continue()
        } else {
          resolve(projects)
        }
      }
      request.onerror = () => reject(new Error('Failed to get projects'))
    })
  }

  async updateProject(id: string, data: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite')
      const store = transaction.objectStore('projects')
      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const project = getRequest.result as StoredProject
        if (project) {
          project.data = data
          project.updatedAt = new Date()
          const putRequest = store.put(project)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(new Error('Failed to update project'))
        } else {
          reject(new Error('Project not found'))
        }
      }
      getRequest.onerror = () => reject(new Error('Failed to get project'))
    })
  }

  async deleteProject(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite')
      const store = transaction.objectStore('projects')
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(new Error('Failed to delete project'))
    })
  }
}